<template>
  <aside class="editor-palette" aria-label="编辑器左侧面板">
    <nav class="editor-palette__rail" aria-label="编辑器左侧工具">
      <el-tooltip v-for="panel in panels" :key="panel.id" :content="panel.label" placement="right" popper-class="editor-rail-tooltip">
        <button type="button" class="editor-rail-button" :class="{ 'is-active': activePanel === panel.id }" :aria-label="'打开' + panel.label + '面板'" :aria-controls="'editor-panel-' + panel.id" :aria-pressed="String(activePanel === panel.id)" @click="activePanel = panel.id"><i aria-hidden="true" :class="panel.icon"></i><span class="editor-rail-button__label">{{ panel.label }}</span></button>
      </el-tooltip>
      <span class="editor-rail-spacer"></span>
      <el-popover placement="right-end" title="编辑器帮助" width="240" trigger="click">
        <p>从左侧拖入组件或图片，也可双击、按 Enter 添加。组件、图片和模板分类可展开或收起。</p>
        <p>Ctrl＋滚轮缩放画布，中键按住平移，Delete 删除选中组件，Ctrl+Z 撤销。</p>
        <button slot="reference" type="button" class="editor-rail-button" aria-label="查看编辑器帮助"><i class="el-icon-question" aria-hidden="true"></i><span class="editor-rail-button__label">帮助</span></button>
      </el-popover>
    </nav>
    <div class="editor-palette__body">
      <section v-show="activePanel === 'components'" id="editor-panel-components" class="editor-palette-panel" aria-label="组件面板">
        <el-input size="small" v-model="search" placeholder="搜索组件" label="搜索组件" prefix-icon="el-icon-search" clearable class="editor-palette__search"></el-input>
        <div class="editor-palette-meta"><span>组件库</span><span>拖入画布</span></div>
        <div class="editor-palette__scroll">
          <p v-if="resourceCatalogLoading" class="editor-component-feedback" role="status">分类加载中…</p>
          <p v-if="resourceCatalogError" class="editor-component-feedback" role="alert">{{ resourceCatalogError }} <button type="button" @click="loadComponentCatalogs">重试分类</button></p>
          <section v-for="section in sections" :key="section.id" class="editor-component-section" :aria-busy="String(sectionLoading(section))">
            <button type="button" class="editor-component-section__toggle" :aria-expanded="String(isExpanded(section))" :aria-controls="'editor-category-' + section.id" :aria-label="(isExpanded(section) ? '收起' : '展开') + section.label + '分类'" :title="section.path" @click="toggleSection(section)">
              <i aria-hidden="true" class="el-icon-arrow-right" :class="{ 'is-expanded': isExpanded(section) }"></i>{{ section.label }}
            </button>
            <div v-if="isExpanded(section)" :id="'editor-category-' + section.id" class="editor-component-grid">
              <button type="button" v-for="item in section.items" :key="item.key" class="editor-component-card" :class="{ 'editor-component-card--map': !!item.cover }" draggable="true" :aria-label="'添加' + item.label" :title="item.label + '：' + (item.description ? item.description + '；' : '') + '拖入画布，或双击添加'" @dragstart="startDrag($event, item)" @dragend="endDrag" @dblclick="addToCenter(item)" @keydown.enter.prevent="addToCenter(item)">
                <resource-cover v-if="item.resource || (item.builtinId && !item.cover)" class="editor-component-card__cover" :resource-id="item.resource ? (item.resource.version === 1 ? item.resource.id : undefined) : item.builtinId" :file-id="item.resource && item.resource.coverFileId" :name="item.label" :height="64" background="#363636" />
                <span v-else-if="item.cover" class="editor-component-card__image"><img :src="publicPath + item.cover" :alt="item.label + '预设封面'" :draggable="false" loading="lazy" /></span>
                <span v-else class="editor-component-card__image" :class="'is-' + item.option.component.prop">
                  <svg v-if="['bar', 'line', 'common', 'pictorialbar'].includes(item.option.component.prop)" viewBox="0 0 100 60" aria-hidden="true">
                    <path class="mini-axis" d="M8 6 V52 H94" />
                    <g v-if="item.option.component.prop !== 'line'" fill="currentColor"><rect x="18" y="29" width="10" height="22"/><rect x="38" y="17" width="10" height="34"/><rect x="58" y="25" width="10" height="26"/><rect x="78" y="9" width="10" height="42"/></g>
                    <path v-if="item.option.component.prop !== 'bar'" d="M15 38 L37 15 L58 29 L84 10" fill="none" stroke="#4ed8dc" stroke-width="2"/>
                  </svg>
                  <svg v-else-if="['productList', 'deviceList'].includes(item.option.component.prop)" viewBox="0 0 100 60" aria-hidden="true">
                    <rect x="6" y="6" width="88" height="48" rx="2" fill="#0b202d" stroke="#255267"/>
                    <path d="M12 17 H88 M12 29 H88 M12 41 H88" stroke="#255267"/>
                    <path d="M13 12 H44 M13 23 H54 M13 35 H47 M13 47 H58" stroke="#d8f4ff" stroke-width="2"/>
                    <g fill="#5ee5b0"><circle cx="83" cy="23" r="2"/><circle cx="83" cy="35" r="2"/><circle cx="83" cy="47" r="2"/></g>
                  </svg>
                  <span v-else-if="['pie', 'progress', 'gauge'].includes(item.option.component.prop)" class="mini-ring"></span>
                  <span v-else-if="item.option.component.prop === 'text'" class="mini-text">{{ item.label === '超链接' ? '链接' : '文字' }}</span>
                  <i v-else aria-hidden="true" :class="'iconfont ' + (item.option.icon || 'icon-img')"></i>
                </span>
                <span class="editor-component-card__name">{{ item.label }}</span>
              </button>
            </div>
            <div v-if="isExpanded(section) && section.catalogIds.length" class="editor-component-feedback" :aria-busy="String(sectionLoading(section))">
              <p v-if="sectionLoading(section)" role="status">组件加载中…</p>
              <p v-if="sectionError(section)" role="alert">{{ sectionError(section) }} <button type="button" @click="loadResourceSection(section, false, true)">重试分类</button></p>
              <p v-if="sectionEmpty(section)" role="status">{{ search.trim() ? '没有匹配的组件' : '暂无组件' }}</p>
              <button v-if="sectionHasMore(section) && !sectionError(section)" type="button" :disabled="sectionLoading(section)" @click="loadResourceSection(section, true)">加载更多组件</button>
            </div>
          </section>
          <p v-if="!sections.length" class="editor-empty" role="status">没有匹配的组件</p>
        </div>
      </section>
      <section v-show="activePanel === 'layers'" id="editor-panel-layers" class="editor-palette-panel" aria-label="图层面板">
        <h2 class="editor-panel-heading">图层</h2>
        <div class="editor-layer-toolbar" role="group" aria-label="文件组操作">
          <button type="button" :disabled="!canCreateLayerGroup" :title="layerGroupHint" @click="createLayerGroup"><i class="el-icon-folder-add" aria-hidden="true"></i>新建文件组</button>
          <button type="button" :disabled="!canUngroupLayer" title="解散文件组，保留其中组件" @click="ungroupLayer"><i class="el-icon-folder-opened" aria-hidden="true"></i>解散文件组</button>
        </div>
        <p class="editor-layer-hint" role="status">{{ layerGroupHint }}</p>
        <div class="editor-palette__scroll editor-layer-list">
          <layer :nav="contain.nav"></layer>
          <p v-if="!contain.nav.length" class="editor-empty">暂无图层</p>
        </div>
      </section>
      <section v-if="canQueryImages" v-show="activePanel === 'images'" id="editor-panel-images" class="editor-palette-panel" aria-label="图片面板">
        <image-resources v-if="activePanel === 'images'" compact grouped @use="addResourceImage"></image-resources>
      </section>
      <section v-if="!contain.isComponentTemplateEditing" v-show="activePanel === 'templates'" id="editor-panel-templates" class="editor-palette-panel" aria-label="页面模板面板">
        <h2 id="editor-page-template-heading" class="editor-panel-heading">页面模板</h2>
        <div id="editor-template-content" class="editor-palette__scroll" role="region" aria-labelledby="editor-page-template-heading" tabindex="0">
          <template-resources v-if="activePanel === 'templates' && canQueryTemplates" ref="templates" compact kind="page" grouped @use="addTemplate" />
          <div v-else class="editor-template-empty" role="status">
            <i class="el-icon-collection" aria-hidden="true"></i>
            <strong>模板暂不可用</strong>
            <p>当前账号没有模板查询权限。</p>
          </div>
        </div>
      </section>
    </div>
  </aside>
</template>

<script>
import baseList from '@/option/base'
import { componentSections, COMPONENT_DRAG_TYPE } from '@/editor/core/palette-catalog'
import { componentPaletteSections, componentPaletteItem, DEFAULT_COMPONENT_CATALOG } from '@/editor/core/component-palette'
import { templateCatalogs } from '@/utils/templates'
import { TEMPLATE_RENDERERS } from '@/editor/core/component-template'
import { systemComponent, systemComponentId, systemComponentOption } from '@/editor/core/system-components'
import { beginComponentResourceDrag, endComponentResourceDrag, COMPONENT_RESOURCE_DRAG_TYPE } from '@/editor/core/component-resource'
import { queryTemplates as queryComponents, getTemplateCatalogs } from '@/api/component-resources'
import { componentResourcePage, componentResourceError } from '@/utils/component-resources'
import { createRequestCancellation, isRequestCanceled } from '@/api/http'
import { resourceImageComponent } from '@/editor/core/resource-image'
import ImageResources from '@/components/ImageResources/browser.vue'
import TemplateResources from '@/components/Templates/browser.vue'
import ResourceCover from '@/components/Templates/cover.vue'
import { selectedRoots, canEditNode } from '@/editor/core/node-operations'
import layer from './layer'
import store from '@/store'
export default {
  inject: ['contain'],
  components: { layer, ImageResources, TemplateResources, ResourceCover },
  data: () => ({
    systemRecords: {}, resourceCatalogs: [], resourcePages: {}, resourceCatalogLoading: false, resourceCatalogError: '',
    search: '', activePanel: 'components', expanded: {}, searchExpanded: {}, publicPath: process.env.BASE_URL || '/',
    panelOptions: [
      { id: 'components', label: '组件', icon: 'iconfont icon-bar' },
      { id: 'layers', label: '图层', icon: 'el-icon-tickets' },
      { id: 'images', label: '图片', icon: 'el-icon-picture-outline' },
      { id: 'templates', label: '模板', icon: 'el-icon-menu' }
    ]
  }),
  computed: {
    layerRoots () { return selectedRoots(this.contain.nav, this.contain.active) },
    layerStructureReady () { return !!this.contain.projectRecord && this.contain.editorSaveReady !== false && !this.contain.editorVersionSwitching },
    canCreateLayerGroup () { return this.layerStructureReady && this.layerRoots.length > 0 && this.layerRoots.every(canEditNode) && this.layerRoots.every(item => item.siblings === this.layerRoots[0].siblings) },
    canUngroupLayer () { return this.layerStructureReady && this.layerRoots.length === 1 && !!this.layerRoots[0].node.children && canEditNode(this.layerRoots[0]) },
    layerGroupHint () {
      if (!this.layerStructureReady) return '正在准备编辑器…'
      if (!this.layerRoots.length) return '按 Ctrl 多选组件后创建文件组，选中文件组可整体拖动。'
      if (!this.layerRoots.every(canEditNode)) return '所选图层包含锁定内容，请先解锁。'
      if (!this.layerRoots.every(item => item.siblings === this.layerRoots[0].siblings)) return '请在同一层级选择组件后创建文件组。'
      return '已选 ' + this.layerRoots.length + ' 项，文件组内的组件保持相对位置。'
    },
    canQueryComponents () { return store.getters['auth/hasResourcePermission']('visualizationComponent:query') },
    canQueryTemplates () { return store.getters['auth/hasResourcePermission']('visualizationTemplate:query') },
    sections () {
      const keyword = this.search.trim().toLowerCase()
      let builtins = componentSections(baseList).map(section => ({
        ...section,
        items: section.items.map(item => {
          const resource = this.systemRecords[systemComponentId(item.key)]
          if (resource) return { ...item, resource, label: resource.name, option: systemComponentOption(item.option, resource) }
          const builtin = systemComponent(item)
          return { ...item, builtinId: builtin.snapshot ? builtin.id : undefined, builtinSnapshot: builtin.snapshot }
        }).filter(item => item.label.toLowerCase().includes(keyword))
      }))
      if (this.contain.isComponentTemplateEditing) builtins = builtins.map(section => ({ ...section, items: section.items.filter(item => TEMPLATE_RENDERERS.includes(item.option.component.name) && item.option.component.name === item.option.component.prop) }))
      return componentPaletteSections(builtins, this.canQueryComponents ? this.resourceCatalogs : []).map(section => {
        const items = new Map(section.items.map(item => [item.resource ? 'resource:' + item.resource.id : item.key, item]))
        section.catalogIds.forEach(id => this.resourcePage(id).items.forEach(resource => {
          // 父分类接口包含后代，只把直接登记在默认根的旧资源并入常用。
          if (id !== DEFAULT_COMPONENT_CATALOG || resource.catalogIds.includes(DEFAULT_COMPONENT_CATALOG)) items.set('resource:' + resource.id, componentPaletteItem(resource))
        }))
        return { ...section, items: [...items.values()] }
      }).filter(section => section.items.length || (section.catalogIds.length && (!keyword || section.catalogIds.some(id => { const page = this.resourcePage(id); return !page.loaded || page.loading || page.error || page.hasMore }))))
    },
    canQueryImages () { return store.getters['auth/hasResourcePermission']('visualizationImage:query') },
    panels () { return this.panelOptions.filter(panel => (panel.id !== 'images' || this.canQueryImages) && (panel.id !== 'templates' || !this.contain.isComponentTemplateEditing)) }
  },
  watch: {
    canQueryComponents (allowed) {
      if (allowed) { this.loadSystemResources(); this.loadComponentCatalogs() }
      else {
        if (this._systemCancel) this._systemCancel.cancel()
        if (this._catalogCancel) this._catalogCancel.cancel()
        this.systemRecords = {}; this.resourceCatalogs = []; this.resourceCatalogLoading = false; this.resourceCatalogError = ''
        this.resetResourcePages(); endComponentResourceDrag()
      }
    },
    search () {
      this.searchExpanded = {}; this.resetResourcePages(); clearTimeout(this._resourceSearchTimer)
      this._resourceSearchTimer = setTimeout(() => this.loadExpandedResources(), 250)
    },
    canQueryImages (allowed) { if (!allowed && this.activePanel === 'images') this.activePanel = 'components' },
    'contain.isComponentTemplateEditing' (editing) { if (editing && this.activePanel === 'templates') this.activePanel = 'components' }
  },
  created () { this._resourceGeneration = 0; this._resourceQueue = []; this._resourceActive = 0; this._resourceCancels = new Map() },
  mounted () { this.loadSystemResources(); this.loadComponentCatalogs() },
  beforeDestroy () {
    if (this._systemCancel) this._systemCancel.cancel()
    if (this._catalogCancel) this._catalogCancel.cancel()
    clearTimeout(this._resourceSearchTimer); this.resetResourcePages(); endComponentResourceDrag()
  },
  methods: {
    createLayerGroup () { if (this.canCreateLayerGroup) this.contain.$refs.contentmenu.handleCompose() },
    ungroupLayer () { if (this.canUngroupLayer) this.contain.$refs.contentmenu.handleLogout() },
    resourcePage (id) {
      return this.resourcePages['catalog:' + id] || { items: [], loaded: false, loading: false, error: '', pageIndex: 0, hasMore: false }
    },
    resetResourcePages () {
      this._resourceGeneration = (this._resourceGeneration || 0) + 1
      if (this._resourceCancels) this._resourceCancels.forEach(cancel => cancel.cancel())
      this._resourceQueue = []; this.resourcePages = {}
    },
    async loadComponentCatalogs () {
      if (!this.canQueryComponents) return
      if (this._catalogCancel) this._catalogCancel.cancel()
      const cancel = createRequestCancellation(); this._catalogCancel = cancel
      this.resourceCatalogLoading = true; this.resourceCatalogError = ''
      try {
        const catalogs = templateCatalogs(await getTemplateCatalogs('component', { cancelToken: cancel.token }))
        if (this._isDestroyed || this._catalogCancel !== cancel || !this.canQueryComponents) return
        this.resetResourcePages(); this.resourceCatalogs = catalogs
        await this.$nextTick(); this.loadExpandedResources()
      } catch (error) {
        if (!isRequestCanceled(error) && !this._isDestroyed && this._catalogCancel === cancel && this.canQueryComponents) this.resourceCatalogError = componentResourceError(error)
      } finally {
        if (!this._isDestroyed && this._catalogCancel === cancel) this.resourceCatalogLoading = false
      }
    },
    loadExpandedResources () {
      if (this._isDestroyed || !this.canQueryComponents) return
      this.sections.filter(section => this.isExpanded(section)).forEach(section => this.loadResourceSection(section))
    },
    loadResourceSection (section, more = false, retry = false) {
      if (this._isDestroyed || !this.canQueryComponents) return
      section.catalogIds.forEach(id => {
        const state = this.resourcePage(id)
        if (state.loading || (retry && !state.error) || (!retry && state.loaded && (!more || !state.hasMore))) return
        const pageIndex = more && state.loaded ? state.pageIndex + 1 : 0
        this.$set(this.resourcePages, 'catalog:' + id, { ...state, loading: true, error: '' })
        this._resourceQueue.push({ id, pageIndex, generation: this._resourceGeneration, keyword: this.search.trim() })
      })
      this.pumpResourceQueue()
    },
    pumpResourceQueue () {
      if (this._isDestroyed || !this.canQueryComponents) return
      while (this._resourceActive < 2 && this._resourceQueue.length) {
        const task = this._resourceQueue.shift()
        if (task.generation !== this._resourceGeneration) continue
        this._resourceActive++
        this.fetchResourceCategory(task).finally(() => { this._resourceActive--; this.pumpResourceQueue() })
      }
    },
    async fetchResourceCategory (task) {
      const cancel = createRequestCancellation(); this._resourceCancels.set(task.id, cancel)
      const current = () => !this._isDestroyed && this.canQueryComponents && task.generation === this._resourceGeneration && this._resourceCancels.get(task.id) === cancel
      try {
        const page = componentResourcePage(await queryComponents({ source: 'custom', scope: 'all', catalogId: task.id, keyword: task.keyword, pageIndex: task.pageIndex, pageSize: 24 }, { cancelToken: cancel.token }))
        if (!current()) return
        if (page.pageIndex !== task.pageIndex || page.pageSize !== 24) throw new Error('组件列表已变化，请重新加载此分类')
        if (page.data.some(item => item.isSystem) || new Set(page.data.map(item => item.id)).size !== page.data.length) throw new Error('组件列表范围不一致，请刷新后重试')
        const previous = task.pageIndex ? this.resourcePage(task.id).items : []
        const items = new Map([...previous, ...page.data].map(item => [item.id, item]))
        this.$set(this.resourcePages, 'catalog:' + task.id, { items: [...items.values()], pageIndex: page.pageIndex,
          hasMore: (page.pageIndex + 1) * page.pageSize < page.total, loaded: true, loading: false, error: '' })
      } catch (error) {
        if (current() && !isRequestCanceled(error)) this.$set(this.resourcePages, 'catalog:' + task.id, { ...this.resourcePage(task.id), loading: false, hasMore: false, error: componentResourceError(error) })
      } finally {
        if (this._resourceCancels.get(task.id) === cancel) this._resourceCancels.delete(task.id)
      }
    },
    sectionLoading (section) { return section.catalogIds.some(id => this.resourcePage(id).loading) },
    sectionError (section) { return section.catalogIds.map(id => this.resourcePage(id).error).find(Boolean) || '' },
    sectionHasMore (section) { return section.catalogIds.some(id => this.resourcePage(id).hasMore) },
    sectionEmpty (section) { return !section.items.length && !this.sectionLoading(section) && !this.sectionError(section) && section.catalogIds.every(id => this.resourcePage(id).loaded && !this.resourcePage(id).hasMore) },
    async loadSystemResources () {
      if (!this.canQueryComponents) return
      if (this._systemCancel) this._systemCancel.cancel()
      const cancel = createRequestCancellation(); this._systemCancel = cancel
      const ids = componentSections(baseList).flatMap(section => section.items).map(systemComponent).filter(item => item.snapshot).map(item => item.id)
      try {
        const records = []
        for (let offset = 0; offset < ids.length; offset += 24) {
          const resourceIds = ids.slice(offset, offset + 24)
          const page = componentResourcePage(await queryComponents({ source: 'system', scope: 'public', resourceIds, pageIndex: 0, pageSize: 24 }, { cancelToken: cancel.token }))
          if (this._isDestroyed || this._systemCancel !== cancel || !this.canQueryComponents) return
          if (page.data.some(item => !resourceIds.includes(item.id) || !item.isSystem)) throw new Error('系统组件返回不一致')
          records.push(...page.data)
        }
        this.systemRecords = Object.fromEntries(records.map(item => [item.id, item]))
      } catch (error) { if (!isRequestCanceled(error) && !this._isDestroyed && this._systemCancel === cancel) this.$message.warning('系统组件样式读取失败，暂用内置样式；重新打开编辑器可重试') }
    },
    addTemplate (item) { this.contain.$refs.container.addTemplate(item) },
    refreshTemplates () { if (this.$refs.templates) this.$refs.templates.refresh() },
    isExpanded (section) {
      const states = this.search.trim() ? this.searchExpanded : this.expanded
      return states[section.id] === undefined ? (!!this.search.trim() || !!section.expanded) : states[section.id]
    },
    toggleSection (section) {
      this.$set(this.search.trim() ? this.searchExpanded : this.expanded, section.id, !this.isExpanded(section))
      if (this.isExpanded(section)) this.loadResourceSection(section)
    },
    startDrag (event, item) {
      if (item.resource && !this.canQueryComponents) { event.preventDefault(); return }
      event.dataTransfer.setData(item.resource ? COMPONENT_RESOURCE_DRAG_TYPE : COMPONENT_DRAG_TYPE, item.resource ? beginComponentResourceDrag(item.resource) : item.key)
      event.dataTransfer.effectAllowed = 'copy'
    },
    endDrag () { endComponentResourceDrag() },
    addToCenter (item) { if (item.resource) { if (this.canQueryComponents) this.addTemplate(item.resource) } else this.contain.$refs.container.addComponent(item.option) },
    addResourceImage (image) {
      const option = resourceImageComponent(baseList, image)
      if (option) this.contain.$refs.container.addComponent(option)
    }
  }
}
</script>
